import { BrowserRouter as Router, Routes, Route, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import { useTranslation } from "./hooks/useTranslation";
import Home from "./pages/Home";
import About from "./pages/About";
import Skills from "./pages/Skills";
import Projects from "./pages/Projects";
import Research from "./pages/Research";
import Resume from "./pages/Resume";
import Contact from "./pages/Contact";

function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMenuOpen(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const navItems = [
    { name: t('nav.home'), path: "/" },
    { name: t('nav.about'), path: "/about" },
    { name: t('nav.projects'), path: "/projects" },
    { name: t('nav.skills'), path: "/skills" },
    { name: t('nav.research'), path: "/research" },
    { name: t('nav.resume'), path: "/resume" },
    { name: t('nav.contact'), path: "/contact" }
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-primary/95 backdrop-blur-md border-b border-tertiary/20' : 'bg-transparent'
      }`}
    >
      <div className="container-width">
        <div className="flex justify-between items-center py-3 sm:py-4 px-4 sm:px-0">
          {/* Logo */}
          <Link 
            to="/" 
            className="text-lg sm:text-xl font-bold text-gradient hover:scale-105 transition-transform duration-300 touch-target"
            onClick={() => setIsMenuOpen(false)}
          >
            Anjali Barge
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`relative text-sm font-medium transition-colors duration-300 hover:text-accent ${
                  location.pathname === item.path ? 'text-accent' : 'text-muted-light'
                }`}
              >
                {item.name}
                {location.pathname === item.path && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-accent rounded-full"
                  />
                )}
              </Link>
            ))}
            
            {/* Language Switcher - Desktop */}
            <div className="flex items-center space-x-2 ml-4 border-l border-tertiary/20 pl-6">
              <button
                onClick={() => setLanguage('ja')}
                className={`text-sm font-medium transition-colors duration-300 ${
                  language === 'ja' ? 'text-accent' : 'text-muted-light hover:text-accent'
                }`}
                aria-label="Switch to Japanese"
              >
                日本語
              </button>
              <span className="text-muted-light">|</span>
              <button
                onClick={() => setLanguage('en')}
                className={`text-sm font-medium transition-colors duration-300 ${
                  language === 'en' ? 'text-accent' : 'text-muted-light hover:text-accent'
                }`}
                aria-label="Switch to English"
              >
                EN
              </button>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-surface-light transition-colors focus:outline-none focus:ring-2 focus:ring-accent"
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            <div className="w-6 h-6 flex flex-col justify-center items-center">
              <span className={`w-5 h-0.5 bg-white transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-1.5' : ''}`} />
              <span className={`w-5 h-0.5 bg-white transition-all duration-300 mt-1 ${isMenuOpen ? 'opacity-0' : ''}`} />
              <span className={`w-5 h-0.5 bg-white transition-all duration-300 mt-1 ${isMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`} />
            </div>
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-tertiary/20 bg-primary/95 backdrop-blur-md"
            >
              <div className="py-4 space-y-1 max-h-screen overflow-y-auto">
                {navItems.map((item, index) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={item.path}
                      className={`block py-3 px-4 mx-2 rounded-lg transition-all duration-200 text-base font-medium ${
                        location.pathname === item.path 
                          ? 'text-accent bg-accent/10 border-l-4 border-accent' 
                          : 'text-muted-light hover:text-accent hover:bg-surface-light active:bg-accent/5'
                      }`}
                    >
                      {item.name}
                    </Link>
                  </motion.div>
                ))}
                
                {/* Mobile Contact Info */}
                <div className="pt-4 mt-4 border-t border-tertiary/20 px-4">
                  {/* Language Switcher - Mobile */}
                  <div className="flex items-center justify-center space-x-4 mb-4 pb-4 border-b border-tertiary/20">
                    <button
                      onClick={() => setLanguage('ja')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        language === 'ja' 
                          ? 'text-accent bg-accent/10 border border-accent' 
                          : 'text-muted-light hover:text-accent hover:bg-surface-light'
                      }`}
                      aria-label="Switch to Japanese"
                    >
                      日本語
                    </button>
                    <button
                      onClick={() => setLanguage('en')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        language === 'en' 
                          ? 'text-accent bg-accent/10 border border-accent' 
                          : 'text-muted-light hover:text-accent hover:bg-surface-light'
                      }`}
                      aria-label="Switch to English"
                    >
                      EN
                    </button>
                  </div>
                  
                  <div className="flex flex-col space-y-2 text-sm text-muted">
                    <a 
                      href="mailto:bargeanjali650@gmail.com" 
                      className="hover:text-accent transition-colors"
                    >
                      📧 bargeanjali650@gmail.com
                    </a>
                    <div className="flex space-x-4 pt-2">
                      <a 
                        href="https://github.com/AB2511" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:text-accent transition-colors"
                      >
                        GitHub
                      </a>
                      <a 
                        href="https://linkedin.com/in/anjali-barge" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:text-accent transition-colors"
                      >
                        LinkedIn
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
}

function Footer() {
  const { t } = useTranslation();
  
  return (
    <footer className="bg-surface-light border-t border-tertiary/20">
      <div className="container-width py-8">
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="text-muted text-sm">
            {t('footer.copyright')}
          </div>
          <div className="flex space-x-6">
            <a href="https://github.com/AB2511" target="_blank" rel="noopener noreferrer" 
               className="text-muted hover:text-accent transition-colors">
              GitHub
            </a>
            <a href="https://linkedin.com/in/anjali-barge" target="_blank" rel="noopener noreferrer"
               className="text-muted hover:text-accent transition-colors">
              LinkedIn
            </a>
            <a href="https://orcid.org/0009-0005-1983-9214" target="_blank" rel="noopener noreferrer"
               className="text-muted hover:text-accent transition-colors">
              ORCID
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <Router>
        <div className="min-h-screen flex flex-col bg-primary">
          <Navigation />
          
          <main className="flex-grow pt-20">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/skills" element={<Skills />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/research" element={<Research />} />
              <Route path="/resume" element={<Resume />} />
              <Route path="/contact" element={<Contact />} />
            </Routes>
          </main>

          <Footer />
        </div>
      </Router>
    </LanguageProvider>
  );
}